// <pfs-overlay-host>: Singleton App-Root-Overlay (Scrim + zentrierte Card).
//
// PFS-580 — Pascal-Idee 2026-06-19. Deckt die GANZE App, nicht den Viewport:
// der Host sitzt `position:absolute; inset:0` IN der `position:relative`
// pfs-shell → standalone = Seitengröße, im WP-Embed nur die Embed-Region
// (kein Escape über die WP-Seite). Sidebar+Topbar+Content werden überdeckt.
//
// Generisch + event-driven (Schwester zu <pfs-toast-host>/<pfs-banner-host>).
// Erste Nutzung = Session-Ablauf/Logout (Countdown + „Neu anmelden"), aber
// beliebiger Card-Inhalt (App-Loading, Wartung, Blockier-Dialog). Nur EIN
// Overlay gleichzeitig (modal/blockierend).
//
// Konsumer-API:
//
//   window.dispatchEvent(new CustomEvent("pfs:overlay", {
//     detail: {
//       id: "session-expired",        // optional, default random; gleiche id → replace
//       title: "Sitzung läuft ab",    // optional
//       message: "Bitte neu anmelden", // optional
//       variant: "warning",            // "warning"|"info"|"error"|"success", default "warning"
//       scrim: "dark",                 // PFS-580: "dark"|"blur"|"none". Default "dark".
//       layout: "card",                // PFS-580: "card"|"band". Default "card".
//       countdownSeconds: 10,          // 0 = kein Countdown. Default 0.
//       primary: { label: "Jetzt neu anmelden", href: "/auth/login" },
//       secondary: { label: "Abbrechen" },
//       dismissible: false,            // X + Scrim-Klick schließen. Default false (blockierend).
//     },
//     bubbles: true, composed: true,
//   }));
//
//   window.dispatchEvent(new CustomEvent("pfs:overlay-close", { detail: { id } }));
//
// Events out (composed, cancelable — preventDefault unterdrückt das Default):
//   pfs-overlay:primary-clicked  { id }   — default: navigate primary.href + close
//   pfs-overlay:countdown-done   { id }   — default: navigate primary.href + close
//   pfs-overlay:dismissed        { id, reason }  — nach dem Schließen
//                                  (reason: "primary"|"secondary"|"dismiss"|"countdown"|"programmatic")

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";

export interface PfsOverlayButton {
  label: string;
  href?: string;
}

export interface PfsOverlayDetail {
  id?: string;
  title?: string;
  message?: string;
  variant?: "warning" | "info" | "error" | "success";
  scrim?: "dark" | "blur" | "none";
  countdownSeconds?: number;
  primary?: PfsOverlayButton;
  secondary?: PfsOverlayButton;
  dismissible?: boolean;
  /**
   * PFS-580: Slot-Modus für BELIEBIGEN Inhalt. Setzt man `slot: "x"`, rendert die
   * Card statt `message` ein `<slot name="x">` — der Konsument projiziert dort
   * beliebiges Light-DOM (eigenes WC, Formular, Markup) als Kind des Hosts:
   *   <pfs-overlay-host><my-thing slot="x"></my-thing></pfs-overlay-host>
   * Sicher (Projektion, kein innerHTML/XSS). title/buttons/countdown bleiben als
   * optionale Chrome drumherum.
   */
  slot?: string;
  /**
   * PFS-580: Layout-Form. "card" (default) = breitenbegrenzte, zentrierte Card.
   * "band" = horizontaler Streifen über die VOLLE Container-Breite (vertikal
   * zentriert, edge-to-edge): Titel/Message links, Countdown + Buttons rechts.
   * Unabhängig vom `scrim` (jede Kombination möglich).
   */
  layout?: "card" | "band";
}

const OPEN_EVENT = "pfs:overlay";
const CLOSE_EVENT = "pfs:overlay-close";

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  /* App-Root-scoped: füllt die positionierte pfs-shell, nicht den Viewport.
     Über banner(10001)/toast(10000) — das Overlay ist modal. */
  position: absolute;
  inset: 0;
  z-index: 10050;
  display: none;
  pointer-events: none;
  font-family: inherit;
}
:host([data-open]) { display: grid; place-items: center; }

.scrim {
  position: absolute;
  inset: 0;
  pointer-events: auto;            /* blockiert die App darunter */
  background: var(--pfs-overlay-scrim-bg, rgba(15, 13, 10, 0.5));
  animation: pfs-overlay-fade 0.15s ease-out;
}
:host([data-scrim="blur"]) .scrim {
  background: var(--pfs-overlay-scrim-bg, rgba(15, 13, 10, 0.2));
  backdrop-filter: blur(5px);
}
:host([data-scrim="none"]) .scrim { background: transparent; }

.card {
  position: relative;
  pointer-events: auto;
  z-index: 1;
  width: min(26rem, calc(100% - 2rem));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* PFS-733: Modal-Content-Gap (Titel/Message/Actions = Block-Level) via Token
     statt hardcoded 0.9rem — Section-Rhythmus, kein Ad-hoc-Abstand. */
  gap: var(--pfs-space-section, 1rem);
  text-align: center;
  padding: 1.75rem;
  background: var(--pfs-color-surface);
  color: var(--pfs-color-on-surface);
  /* PFS-580: EIN Akzent-Token für Border + Ring + Button → voll brandbar.
     Default = Brand-Primary (mednet blau, sparkden orange); semantische Varianten
     (info/warning/error/success) überschreiben unten. Kein hardcodetes Orange mehr. */
  --overlay-accent: var(--pfs-color-primary);
  border: 1px solid var(--pfs-color-outline);
  border-top: 4px solid var(--overlay-accent);
  border-radius: var(--pfs-radius-lg, 0.9rem);
  box-shadow: var(--pfs-shadow-overlay, 0 18px 50px rgba(0, 0, 0, 0.28));
  animation: pfs-overlay-pop 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
:host([data-variant="info"])    .card { --overlay-accent: var(--pfs-color-info-container-border); }
:host([data-variant="error"])   .card { --overlay-accent: var(--pfs-color-danger-container-border); }
:host([data-variant="success"]) .card { --overlay-accent: var(--pfs-color-success-container-border); }
:host([data-variant="warning"])  .card { --overlay-accent: var(--pfs-color-warning); }

/* PFS-580: Band-Layout — Full-Width-Streifen statt Card. Inhalt als ZENTRIERTE
   Gruppe (CSS-Grid über die flachen Card-Kinder, DOM unverändert): Countdown-Ring
   links · Titel+Text mittig · Button rechts. Vertikal zentriert (Host place-items),
   horizontal edge-to-edge (width:100%, kein Seiten-Border/Radius). */
:host([data-layout="band"]) .card {
  width: 100%;
  max-width: 100%;
  display: grid;
  grid-template-columns: auto auto auto;
  grid-template-areas:
    "ring title   actions"
    "ring message actions";
  justify-content: center;        /* zentriert die Gruppe im Band */
  align-items: center;
  column-gap: 1.25rem;
  row-gap: 0;
  text-align: center;
  padding: 0.85rem 1.9rem;
  border-radius: 0;
  border-left: 0;
  border-right: 0;
  border-top: 3px solid var(--overlay-accent);
  border-bottom: 3px solid var(--overlay-accent);
}
:host([data-layout="band"]) .ring     { grid-area: ring; --pfs-ring-size: 2.9rem; }
:host([data-layout="band"]) .title    { grid-area: title; align-self: end; }
:host([data-layout="band"]) .message  { grid-area: message; align-self: start; }
:host([data-layout="band"]) .actions  { grid-area: actions; flex-wrap: nowrap; }
:host([data-layout="band"]) .body     { grid-column: 1 / -1; }   /* Slot-Modus = volle Breite */

.title { font-size: 1.15rem; font-weight: 600; margin: 0; }
.message { font-size: 0.9rem; color: var(--pfs-color-on-surface-subtle); margin: 0; }
.body { width: 100%; }   /* PFS-580 Slot-Modus: geslotteter Beliebig-Inhalt */
/* PFS-580: Countdown rendert als <pfs-countdown-ring> (Sekunden im Kreis). Größe
   via --pfs-ring-size (Card-Default; Band überschreibt kleiner). Ring-Farbe folgt
   dem Variant-Akzent. */
.ring {
  --pfs-ring-size: 3.6rem;
  --pfs-ring-color: var(--overlay-accent);
  line-height: 0;
}
.actions { display: flex; gap: 0.6rem; flex-wrap: wrap; justify-content: center; }
${BUTTON_CSS}
/* Variant-Severity-Faerbung des Primary-Buttons behalten (info/error/success/
   warning setzen --overlay-accent); nur die Form kommt aus BUTTON_CSS. */
.btn.primary { background: var(--overlay-accent); border-color: var(--overlay-accent); }
.btn.primary:hover { filter: brightness(0.95); }
.dismiss {
  position: absolute; top: 0.5rem; right: 0.6rem;
  font: inherit; font-size: 1.2rem; line-height: 1; cursor: pointer;
  background: none; border: 0; color: var(--pfs-color-on-surface-subtle);
  padding: 0.25rem;
}

@keyframes pfs-overlay-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes pfs-overlay-pop { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .scrim, .card { animation: none; } }
`;

interface OverlayState {
  id: string;
  detail: PfsOverlayDetail;
  timerId: ReturnType<typeof setInterval> | null;
  remaining: number;
  countdownEl: HTMLElement | null;
}

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

defineComponent({
  tagName: "pfs-overlay-host",
  mixins: [withShadow()],
  render(host: HTMLElement, ctx) {
    const root = ctx.shadowRoot;
    if (!root || host.hasAttribute("data-pfs-overlay-wired")) return;
    host.setAttribute("data-pfs-overlay-wired", "");

    // Styles via adoptedStyleSheets — überleben das root.replaceChildren beim
    // open/close (ein <style>-Child würde dabei mitgelöscht).
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(STYLES);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];

    let state: OverlayState | null = null;

    const emit = (name: string, detail: Record<string, unknown>): boolean => {
      const ev = new CustomEvent(`pfs-overlay:${name}`, {
        detail,
        bubbles: true,
        composed: true,
        cancelable: true,
      });
      host.dispatchEvent(ev);
      return !ev.defaultPrevented;
    };

    const clearTimer = (): void => {
      if (state?.timerId != null) {
        clearInterval(state.timerId);
        state.timerId = null;
      }
    };

    const close = (reason: string): void => {
      if (!state) return;
      const id = state.id;
      clearTimer();
      root.replaceChildren();
      host.removeAttribute("data-open");
      host.removeAttribute("data-variant");
      host.removeAttribute("data-scrim");
      host.removeAttribute("data-layout");
      state = null;
      emit("dismissed", { id, reason });
    };

    const navigatePrimary = (): void => {
      const href = state?.detail.primary?.href;
      if (href) window.location.assign(href);
    };

    const open = (detail: PfsOverlayDetail): void => {
      clearTimer();
      const id = detail.id ?? `overlay-${Math.random().toString(36).slice(2, 10)}`;
      const variant = detail.variant;
      const scrim = detail.scrim ?? "dark";
      const layout = detail.layout ?? "card";
      const dismissible = detail.dismissible === true;
      const cd = Math.max(0, Math.floor(detail.countdownSeconds ?? 0));

      host.setAttribute("data-open", "");
      if (variant) host.setAttribute("data-variant", variant);
      host.setAttribute("data-scrim", scrim);
      host.setAttribute("data-layout", layout);

      const scrimEl = document.createElement("div");
      scrimEl.className = "scrim";
      if (dismissible) scrimEl.addEventListener("click", () => close("dismiss"));

      const card = document.createElement("div");
      card.className = "card";
      card.setAttribute("role", "alertdialog");
      card.setAttribute("aria-modal", "true");

      const parts: string[] = [];
      if (dismissible) parts.push(`<button class="dismiss" data-dismiss aria-label="Schließen">×</button>`);
      if (detail.title) parts.push(`<p class="title">${esc(detail.title)}</p>`);
      if (cd > 0)
        parts.push(
          `<pfs-countdown-ring class="ring" data-remaining="${cd}" data-total="${cd}" data-format="seconds" data-label-placement="inside"></pfs-countdown-ring>`,
        );
      if (detail.slot) parts.push(`<div class="body"><slot name="${esc(detail.slot)}"></slot></div>`);
      else if (detail.message) parts.push(`<p class="message">${esc(detail.message)}</p>`);
      const btns: string[] = [];
      if (detail.primary) btns.push(`<button class="btn primary" data-primary>${esc(detail.primary.label)}</button>`);
      if (detail.secondary) btns.push(`<button class="btn secondary" data-secondary>${esc(detail.secondary.label)}</button>`);
      if (btns.length) parts.push(`<div class="actions">${btns.join("")}</div>`);
      card.innerHTML = parts.join("");

      card.querySelector("[data-primary]")?.addEventListener("click", () => {
        const id2 = state?.id ?? id;
        if (emit("primary-clicked", { id: id2 })) {
          navigatePrimary();
          close("primary");
        }
      });
      card.querySelector("[data-secondary]")?.addEventListener("click", () => close("secondary"));
      card.querySelector("[data-dismiss]")?.addEventListener("click", () => close("dismiss"));

      root.replaceChildren(scrimEl, card);

      state = { id, detail, timerId: null, remaining: cd, countdownEl: null };

      // PFS-580: Countdown läuft jetzt IM <pfs-countdown-ring> (eigener Timer +
      // Label im Kreis). Wir hören nur sein pfs:countdown-done und triggern dieselbe
      // Default-Aktion wie zuvor (emit countdown-done → navigate primary + close);
      // preventDefault am countdown-done unterdrückt das. Der Ring stoppt von selbst,
      // sobald close() ihn via replaceChildren aus dem DOM nimmt (disconnect).
      if (cd > 0) {
        card.querySelector(".ring")?.addEventListener("pfs:countdown-done", () => {
          const id2 = state?.id ?? id;
          if (emit("countdown-done", { id: id2 })) {
            navigatePrimary();
            close("countdown");
          }
        });
      }
    };

    const onOpen = (event: Event): void => {
      const d = (event as CustomEvent<PfsOverlayDetail>).detail;
      if (!d) return;
      open(d);
    };
    const onClose = (event: Event): void => {
      const id = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (state && (id == null || id === state.id)) close("programmatic");
    };

    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener(CLOSE_EVENT, onClose);
    ctx.cleanup.push(() => {
      clearTimer();
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener(CLOSE_EVENT, onClose);
    });
  },
});
